import React, { useState, useEffect } from 'react';
import { Activity, RefreshCw, ArrowRight, AlertCircle } from 'lucide-react';
import { API_BASE_URL } from '../../config';
import { ProviderLogo, fmtBytes } from './DMSDashboard';
import './DMSDashboard.css';

// Extracted from DMSDashboard.js to resolve providers for raw jobs
const getProviderDisplayName = (name = '') => {
    const p = name.toLowerCase();
    if (p.includes('aws') || p.includes('amazon')) return 'AWS';
    if (p.includes('gcp') || p.includes('google')) return 'GCP';
    if (p.includes('azure') || p.includes('microsoft')) return 'Azure';
    return 'On-Premise';
};

const resolveProvider = (connId, connObj, connections = []) => {
    let provider = connObj?.cloud_provider;
    if (!provider) {
        const c = connections.find(x => String(x.id) === String(connId));
        provider = c?.cloud_provider;
        if (!provider && c?.database_engine) {
            const de = c.database_engine.toLowerCase();
            if (de.includes('amazon') || de.includes('rds') || de.includes('aurora')) provider = 'aws';
            else if (de.includes('cloud sql') || de.includes('google') || de.includes('gcp')) provider = 'gcp';
            else if (de.includes('azure')) provider = 'azure';
        }
    }
    return provider || 'on-premise';
};

const MigrationDetailsModal = ({ job, connections = [], onClose }) => {
    const [jobMetrics, setJobMetrics] = useState(null);
    const [metricsLoading, setMetricsLoading] = useState(false);

    useEffect(() => {
        if (!job) { setJobMetrics(null); return; }
        let alive = true;
        const load = async () => {
            setMetricsLoading(true);
            try {
                const res = await fetch(`${API_BASE_URL}/api/dms/metrics/${job.id}`);
                const d = await res.json();
                if (alive && d.success) setJobMetrics(d);
            } catch { } finally { if (alive) setMetricsLoading(false); }
        };
        load();
        const poll = (job.status === 'Running' || job.status === 'running') ? setInterval(load, 5000) : null;
        return () => { alive = false; if (poll) clearInterval(poll); };
    }, [job]);

    if (!job) return null;

    // Process job data if it's a raw job (e.g. from DMSMigrations)
    let displayJob = { ...job };
    if (!displayJob.sourceProvider) {
        const sl = (job.status || '').toLowerCase();
        let status = 'Pending';
        if (['completed', 'success'].includes(sl)) status = 'Completed';
        else if (['failed', 'error'].includes(sl)) status = 'Failed';
        else if (['running', 'syncing', 'cdc_syncing'].includes(sl)) status = 'Running';
        else if (['cancelled', 'stopped', 'suspended'].includes(sl)) status = 'Cancelled';

        displayJob.status = status;
        displayJob.sourceProvider = resolveProvider(job.source_connection_id, job.source_connection, connections);
        displayJob.targetProvider = resolveProvider(job.target_connection_id, job.target_connection, connections);
        
        const bytes = parseInt(job.total_bytes) || 0;
        displayJob.dataSize = fmtBytes(bytes) || '—';
        
        const startedAt = job.started_at ? new Date(job.started_at) : null;
        const completedAt = job.completed_at ? new Date(job.completed_at) : null;
        displayJob.startTime = startedAt ? startedAt.toLocaleString() : new Date(job.created_at || 0).toLocaleString();
        displayJob.endTime = completedAt ? completedAt.toLocaleString() : status === 'Running' ? 'Active' : '—';
        
        const durationMs = startedAt ? Math.max(0, (completedAt || new Date()) - startedAt) : 0;
        displayJob.duration = durationMs > 0
            ? (() => { const h = Math.floor(durationMs / 3600000), m = Math.floor((durationMs % 3600000) / 60000); return h > 0 ? `${h}h ${m}m` : `${m}m`; })()
            : '—';
    }

    const liveThroughput = jobMetrics?.job?.speed_bps ? fmtBytes(jobMetrics.job.speed_bps) + '/s' : (displayJob.options?.throughput || null);
    const liveLag = jobMetrics?.job?.current_lag_ms != null ? parseFloat(jobMetrics.job.current_lag_ms).toFixed(0) + ' ms' : (displayJob.options?.replication_lag || null);
    const liveBytes = jobMetrics?.job?.total_bytes ? fmtBytes(jobMetrics.job.total_bytes) : null;
    const badgeCls = s => ({ Running: 'running', Completed: 'completed', Failed: 'failed', Cancelled: 'cancelled', Pending: 'pending' }[s] || '');

    return (
        <div className="telemetry-modal-overlay" onClick={onClose}>
            <div className="telemetry-modal-content" onClick={e => e.stopPropagation()}>
                <div className="drilldown-hdr">
                    <div className="drilldown-title">
                        <Activity size={16} style={{ color: '#818cf8' }} />
                        <span style={{ fontSize: '1.25rem', fontWeight: 900, color: 'var(--d-text-primary)' }}>{displayJob.name || `Pipeline [${displayJob.id}]`}</span>
                        {metricsLoading && <RefreshCw size={12} className="spin" style={{ color: 'var(--d-text-muted)' }} />}
                    </div>
                    <button className="drilldown-close" onClick={onClose}>×</button>
                </div>

                <div className="telemetry-modal-body">
                    {/* Meta strip containing primary real-time metrics */}
                    <div className="drilldown-meta-strip">
                        <div className="drilldown-meta-card">
                            <span className="dm-label" style={{ fontWeight: 800, color: 'var(--d-text-primary)' }}>Status</span>
                            <span className={`d-badge d-badge-${badgeCls(displayJob.status)}`} style={{ fontWeight: 800, alignSelf: 'flex-start', width: 'fit-content', padding: '0.2rem 0.6rem' }}>{displayJob.status}</span>
                        </div>
                        <div className="drilldown-meta-card">
                            <span className="dm-label" style={{ fontWeight: 800, color: 'var(--d-text-primary)' }}>Throughput</span>
                            <span className="dm-val dm-cyan" style={{ fontWeight: 800 }}>{liveThroughput || '—'}</span>
                        </div>
                        <div className="drilldown-meta-card">
                            <span className="dm-label" style={{ fontWeight: 800, color: 'var(--d-text-primary)' }}>Replication Lag</span>
                            <span className="dm-val dm-red" style={{ fontWeight: 800 }}>{liveLag || '—'}</span>
                        </div>
                        <div className="drilldown-meta-card">
                            <span className="dm-label" style={{ fontWeight: 800, color: 'var(--d-text-primary)' }}>Data Migrated</span>
                            <span className="dm-val dm-green" style={{ fontWeight: 900 }}>{liveBytes || displayJob.dataSize}</span>
                        </div>
                    </div>

                    {/* Detailed static and session metadata */}
                    <div className="drilldown-detail-grid">
                        {[
                            ['Migration Path', <span style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 800, color: 'var(--d-text-primary)' }} key="path">
                                {displayJob.sourceProvider && <ProviderLogo name={displayJob.sourceProvider} size={36} className="provider-logo-glow" />}
                                <ArrowRight size={12} style={{ opacity: 0.8, margin: '0 4px', color: 'var(--d-text-primary)' }} />
                                {displayJob.targetProvider && <ProviderLogo name={displayJob.targetProvider} size={36} className="provider-logo-glow" />}
                            </span>],
                            ['Job Type', <span style={{ color: '#818cf8', textTransform: 'uppercase', fontSize: '0.8rem', fontWeight: 800 }} key="type">{displayJob.job_type || '—'}</span>],
                            ['Started Time', <span style={{ fontWeight: 'normal', color: 'var(--d-text-primary)' }} key="start">{displayJob.startTime}</span>],
                            ['Ended Time', <span style={{ fontWeight: 'normal', color: 'var(--d-text-primary)' }} key="end">{displayJob.endTime}</span>],
                            ['Duration', <span style={{ fontWeight: 800, color: '#38bdf8' }} key="duration">{displayJob.duration}</span>]
                        ].map(([lbl, val]) => (
                            <div key={lbl} className="drilldown-detail-item">
                                <span className="dd-label" style={{ fontWeight: 800, color: 'var(--d-text-primary)' }}>{lbl}</span>
                                <span className="dd-val">{val}</span>
                            </div>
                        ))}
                    </div>

                    {/* Error Log */}
                    {displayJob.status === 'Failed' && (
                        <div className="error-console" style={{ marginTop: '1rem' }}>
                            <div className="error-console-hdr">
                                <AlertCircle size={12} style={{ color: '#f87171' }} />
                                <span>ENGINE FAILURE LOG</span>
                            </div>
                            <div className="error-console-body">
                                {jobMetrics?.errorFeed?.length > 0
                                    ? jobMetrics.errorFeed.map((e, i) => <code key={i}>[{(e.level || 'ERROR').toUpperCase()}] {new Date(e.timestamp).toLocaleTimeString()}: {e.message}</code>)
                                    : <>
                                        <code>[ERROR] Unable to connect to source endpoint. Verify credentials and network access.</code>
                                        <code>[FATAL] Replication aborted after exhausting retry attempts.</code>
                                    </>}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MigrationDetailsModal;
